import { useQuery } from "@tanstack/react-query";
import { Coins } from "lucide-react";
import { fetchCarry } from "@/lib/api";

// Ranks FX by central-bank policy rate → carry vs USD (long high-yielder funded in USD).
export default function CarryRanking() {
  const q = useQuery({
    queryKey: ["carry"],
    queryFn: fetchCarry,
    staleTime: 1000 * 60 * 60 * 3,
    refetchOnWindowFocus: false,
  });
  const d = q.data;
  const maxRate = d ? Math.max(...d.rows.map((r) => r.rate || 0), 0.1) : 1;

  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="carry-ranking-card">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5">
          <Coins size={11} strokeWidth={1.5} className="text-amber-term" /> Carry Ranking · Policy Rates
        </span>
        <span className="text-[11px] uppercase tracking-widest text-zinc-600">carry vs USD</span>
      </div>

      {q.isLoading ? (
        <div className="h-[140px] flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">Loading carry…</div>
      ) : q.isError ? (
        <div className="h-[140px] flex items-center justify-center text-neg text-[11px] uppercase tracking-widest">Carry unavailable</div>
      ) : (
        <div className="divide-y divide-zinc-900" data-testid="carry-rows">
          {d.rows.map((r) => {
            const pos = (r.carry_vs_usd ?? 0) >= 0;
            return (
              <div key={r.ccy} className="px-3 py-1.5 flex items-center gap-3" data-testid={`carry-${r.ccy}`}>
                <span className="text-[12px] font-semibold text-amber-term w-9">{r.ccy}</span>
                <div className="flex-1 h-1.5 bg-zinc-800 rounded-[1px] overflow-hidden">
                  <div className="h-full bg-amber-500/70" style={{ width: `${Math.max(2, ((r.rate || 0) / maxRate) * 100)}%` }} />
                </div>
                <span className="tabular-nums text-[11px] text-zinc-100 w-12 text-right">{r.rate != null ? `${r.rate.toFixed(2)}%` : "—"}</span>
                <span className={`tabular-nums text-[11px] w-14 text-right ${r.ccy === "USD" ? "text-zinc-600" : pos ? "text-pos" : "text-neg"}`}>
                  {r.ccy === "USD" ? "base" : r.carry_vs_usd != null ? `${pos ? "+" : ""}${r.carry_vs_usd.toFixed(2)}` : "—"}
                </span>
                <span className="text-[11px] uppercase tracking-widest text-zinc-600 w-10 text-right hidden sm:inline" title={`${r.src} · ${r.as_of}`}>
                  {r.live ? "live" : "mtg"}
                </span>
              </div>
            );
          })}
        </div>
      )}
      <p className="px-3 py-2 text-[11px] leading-relaxed text-zinc-600 border-t border-zinc-900">
        Central-bank policy rates. USD/EUR live (FRED/ECB); others = last decision. Positive carry = long that currency funded in USD.
      </p>
    </div>
  );
}
